import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleBottomCenterTextIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import { ReactNode, ReactSVGElement, useState } from 'react'
import { MessageDescriptor, useIntl } from 'react-intl'

import { getCurrentOrganizationId, getCurrentSpaceId } from '@/components/App/selectors'
import FeedbackDialog from '@/components/Feedback/Dialog'
import { Role, useRoles } from '@/components/RolesContainer'
import defaultMessages from '@/messages'
import { useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'
import cn from '@/utilities/cn'

import messages from './messages'

import './index.scss'

import { BoltIcon, ChevronRightIcon, DocumentPlusIcon, PlusIcon, SquaresPlusIcon } from '@heroicons/react/24/solid'
import { capitalize, map, orderBy } from 'lodash'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'

import AdditionalPermissionsModal from '@/components/AdditionalPermissionsModal'
import NewIcon from '@/components/Icons/CustomHeroIcons/outline/NewIcon'
import PhoneMessageIcon from '@/components/Icons/CustomHeroIcons/outline/PhoneMessageIcon'
import RocketLaunchIcon from '@/components/Icons/CustomHeroIcons/solid/RocketLaunchIcon'
import SparklesIcon from '@/components/Icons/CustomHeroIcons/solid/SparklesIcon'
import UserPlusIcon from '@/components/Icons/CustomHeroIcons/solid/UserPlusIcon'
import InfiniteScrollTable from '@/components/InfiniteScrollTable'
import { ColumnType } from '@/components/InfiniteScrollTable/interfaces'
import Loading from '@/components/Loading'
import Button from '@/components/TailwindUI/Button'
import Page from '@/components/TailwindUI/Page'
import { Popover } from '@/components/TailwindUI/Popover'
import StatusIndicator from '@/components/TailwindUI/StatusIndicator'
import useFeatures from '@/components/useFeatures'
import { TRIAL_TENANT, WORKSHOP_AGENTS } from '@/constants/featureToggles'
import AppEditMenu from '@/pages/SpaceAppBuilder/components/AppEditMenu/AppEditMenu'
import NewTemplateVersionTag from '@/pages/SpaceAppBuilder/components/NewTemplateVersionTag/NewTemplateVersionTag'
import { AppImage } from '@/pages/SpaceAppBuilder/LandingPage/components/Apps'
import { getAgentDescription, getAgentInstructions } from '@/pages/SpaceAppBuilder/utilities/agentHelpers'
import { App, FeatureStatus, Template } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { appsQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { chatToDocTemplate } from '@/pages/SpaceKnowledgePage/LandingPage/template/chatToDoc'
import { pageRoutes } from '@/routes'
import isTemplateUpdateAvailable from '@/utilities/isTemplateUpdateAvailable'

import NewHereModal from './NewHereModal'

//TODO: ideally pull all Appbuilder related components into a shared folder

type card = {
  name: string | MessageDescriptor
  description: string | MessageDescriptor
  icon: ((props: any) => ReactSVGElement) | typeof BoltIcon
  className?: string
  color?: string
  chevron?: typeof BoltIcon
  isAllowedAccess: boolean
  onClick?: () => void
}

type Flags = ReturnType<typeof useFeatures>

function HomePage() {
  const intl = useIntl()

  const currentSpaceId = useAppSelector(getCurrentSpaceId)
  const organizationId = useAppSelector(getCurrentOrganizationId)
  const user = useAppSelector(getCurrentUser)
  const flags: Flags = useFeatures()
  const roles = useRoles()
  const history = useHistory()

  const isWorkshopAppOwner = roles.hasWorkshopAppOwnerRole(currentSpaceId)
  const isWorkshopManager = roles.hasWorkshopManagerRole(currentSpaceId)
  const hasAnyWorkshopRole = isWorkshopAppOwner || isWorkshopManager

  const fetchAppsQuery = useQuery<{ items: App[] }>({
    ...appsQueryOptions({ spaceId: currentSpaceId }),
    enabled: !!currentSpaceId && hasAnyWorkshopRole
  })

  const hours = new Date().getHours()
  const greeting = hours < 12 ? messages.goodMorning : hours < 17 ? messages.goodAfternoon : messages.goodEvening

  const apps = orderBy(
    fetchAppsQuery.data?.items.filter(
      app =>
        (!isWorkshopAppOwner || app.sharedWith?.includes(user.id)) && app.template?.templateId !== chatToDocTemplate.id
    ),
    app => -new Date(app.modifiedDate).valueOf()
  )

  const knowledgeApps = orderBy(
    fetchAppsQuery.data?.items.filter(
      app =>
        app.template?.templateId === chatToDocTemplate.id && (!isWorkshopAppOwner || app.sharedWith?.includes(user.id))
    ),
    app => -new Date(app.modifiedDate).valueOf()
  )

  /* Landing Page Refresh code from this point on */

  const [newHereModal, setNewHereModal] = useState(false)
  const [permissionsModal, setPermissionsModal] = useState<{ isOpen: boolean; requiredRoles?: Role[] }>({
    isOpen: false
  })
  interface PageRoute {
    requiresRoles?: Role[]
    path: string
  }

  const routeOrFlagPermissions = (pageRoute: PageRoute) => {
    const path = pageRoute.path.replace(':spaceId', currentSpaceId)
    roles.isAllowedAccess(pageRoute.requiresRoles, organizationId, currentSpaceId)
      ? history.push(path)
      : setPermissionsModal({ isOpen: true, requiredRoles: pageRoute.requiresRoles })
  }
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  const heroCards: card[] = [
    {
      name: messages.getStartedGuide,
      description: messages.getStartedDetail,
      icon: NewIcon,
      color: 'white',
      className: 'text-emerald-400',
      onClick: () => {
        flags[TRIAL_TENANT]
          ? window.open('https://help.workgrid.com/s/getting-started-free-trial')
          : window.open('https://help.workgrid.com/s/getting-started-champion-guide/')
      },
      chevron: ArrowTopRightOnSquareIcon,
      isAllowedAccess: true
    },
    {
      name: messages.tryAssistant,
      description: messages.tryAssistantDetail,
      icon: SparklesIcon,
      className: 'text-brand-300',
      color: 'white',
      isAllowedAccess: true,
      onClick: () => {
        routeOrFlagPermissions(pageRoutes.SpacePlaygroundPage)
      }
    },
    {
      name: `Install ${agentsFlagOn ? 'agents' : 'apps'}`,
      description: messages.installAppsDetail,
      icon: RocketLaunchIcon,
      className: 'text-amber-500',
      color: 'white',
      isAllowedAccess: roles.isAllowedAccess(
        pageRoutes.SpaceAppBuilderPage.requiresRoles,
        organizationId,
        currentSpaceId
      ),
      onClick: () => {
        routeOrFlagPermissions({
          path: pageRoutes.SpaceAppBuilderCreateAppPage.path,
          requiresRoles: pageRoutes.SpaceAppBuilderPage.requiresRoles
        })
      }
    },
    {
      name: messages.addUsers,
      description: messages.addUsersDetail,
      icon: UserPlusIcon,
      color: 'white',
      className: 'text-cyan-500',
      isAllowedAccess: roles.isAllowedAccess(
        pageRoutes.OrganizationUsersPage.requiresRoles,
        organizationId,
        currentSpaceId
      ),
      onClick: () => {
        routeOrFlagPermissions(pageRoutes.OrganizationUsersPage)
      }
    },
    {
      name: messages.sendNotifications,
      description: messages.sendNotificationsDetail,
      icon: PhoneMessageIcon,
      className: 'text-violet-500',
      color: 'white',
      isAllowedAccess: roles.isAllowedAccess(
        pageRoutes.SpacePublisherPage.requiresRoles,
        organizationId,
        currentSpaceId
      ),
      onClick: () => {
        routeOrFlagPermissions(pageRoutes.SpacePublisherPage)
      }
    },
    {
      name: messages.integrations,
      description: messages.integrationsDetail,
      icon: BoltIcon,
      className: 'text-white',
      isAllowedAccess: roles.isAllowedAccess(
        pageRoutes.SpaceAppBuilderDataSourcesPage.requiresRoles,
        organizationId,
        currentSpaceId
      ),
      onClick: () => {
        routeOrFlagPermissions(pageRoutes.SpaceAppBuilderDataSourcesPage)
      }
    }
  ]

  const seeAllKnowledge = () => {
    routeOrFlagPermissions(pageRoutes.SpaceKnowledgePage)
  }
  const openKnowledge = (id?: string) => {
    hasAnyWorkshopRole
      ? history.push(`/space/${currentSpaceId}/knowledge/documents/${id}`)
      : setPermissionsModal({ isOpen: true, requiredRoles: [Role.WORKSHOP_APP_OWNER, Role.WORKSHOP_MANAGER_ROLE] })
  }

  const seeAllApps = () => {
    routeOrFlagPermissions(pageRoutes.SpaceAppBuilderPage)
  }

  const newAppFromTemplate = () => {
    routeOrFlagPermissions({
      path: pageRoutes.SpaceAppBuilderCreateAppPage.path,
      requiresRoles: pageRoutes.SpaceAppBuilderPage.requiresRoles
    })
  }

  return (
    <>
      <NewHereModal
        isOpen={newHereModal}
        onClose={() => {
          setNewHereModal(false)
        }}
      />
      <AdditionalPermissionsModal
        isOpen={permissionsModal.isOpen}
        onClose={() => setPermissionsModal({ isOpen: false })}
        requiredRoles={permissionsModal.requiredRoles}
      />
      <Page className="max-w-full">
        <header className="relative isolate overflow-hidden bg-brand-900 py-12 sm:py-12">
          <div className="hidden sm:absolute sm:-top-10 sm:right-1/2 sm:-z-10 sm:mr-10 sm:block sm:transform-gpu sm:blur-2xl">
            <div
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
              }}
              className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-tr from-brand-200 to-fuchsia-700 opacity-20"
            />
          </div>
          <div className="absolute -top-52 left-1/2 -z-10 -translate-x-1/2 transform-gpu blur-2xl sm:top-[-28rem] sm:ml-16 sm:translate-x-0 sm:transform-gpu">
            <div
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
              }}
              className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-tr from-brand-300 to-fuchsia-500 opacity-20"
            />
          </div>
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {
              <Button
                className="float-right flex items-center justify-center gap-1 rounded-full bg-zinc-50/10 py-1 pl-2 pr-3 text-zinc-50 shadow-sm hover:bg-zinc-50/50"
                onClick={() => setNewHereModal(true)}
              >
                <QuestionMarkCircleIcon className="h-7 w-7" /> {intl.formatMessage(messages.needHelp)}
              </Button>
            }
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h1 className="text-2xl text-brand-50">
                {intl.formatMessage(greeting)}
                {user?.name?.givenName ? (
                  <>
                    {', '}
                    <span className="text-brand-200">
                      {user.name.givenName}
                      {'!'}
                    </span>
                  </>
                ) : null}
              </h1>
            </div>
            <div className="mx-auto mt-3 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-3 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-8">
              {heroCards.map(card => {
                const name = typeof card.name === 'string' ? card.name : intl.formatMessage(card.name)
                const description =
                  typeof card.description === 'string' ? card.description : intl.formatMessage(card.description)
                card.chevron = card.chevron ?? ChevronRightIcon
                return card.isAllowedAccess ? (
                  <Button
                    key={name}
                    className="group flex min-h-[104px] gap-x-[14px] rounded-xl bg-zinc-50/10 px-6 py-[14px] ring-1 ring-inset ring-white/10 hover:cursor-pointer hover:bg-zinc-50/20 hover:ring-brand-200"
                    onClick={card.onClick}
                  >
                    <card.icon
                      aria-hidden="true"
                      className={cn('h-6 w-6 flex-none', card.className)}
                      color={card?.color}
                    />
                    <div className="w-full text-lg leading-7">
                      <div className="flex w-full justify-between">
                        <h3 className="font-bold text-zinc-50">{name}</h3>
                        <card.chevron
                          aria-hidden="true"
                          className="invisible h-7 w-7 text-zinc-300/60 group-hover:visible"
                        />
                      </div>
                      <p className="text-left text-base text-brand-100">{description}</p>
                    </div>
                  </Button>
                ) : null
              })}
            </div>
            <div className="mx-auto mt-8 max-w-2xl lg:mx-0">
              <h2 className="break-words text-lg font-bold text-zinc-50">{intl.formatMessage(messages.subHeading)}</h2>
              <p className="break-words text-base text-brand-200">{intl.formatMessage(messages.subtitleSubtext)}</p>
              <div className="mt-4 flex-row text-xs font-semibold">
                <Button
                  onClick={() => window.open('https://help.workgrid.com/s/tutorials')}
                  className="inline-flex h-9 items-center justify-center rounded-md border border-brand-600 bg-slate-100 px-3 py-2.5 text-brand-600"
                >
                  {intl.formatMessage(messages.tutorials)}
                </Button>
                <Button
                  onClick={() => window.open('https://help.workgrid.com/s/article/Release-Notes')}
                  className="ml-2 inline-flex h-9 items-center justify-center rounded-md border border-zinc-50/20 bg-zinc-50/20 px-3 py-2.5 text-zinc-50"
                >
                  {intl.formatMessage(messages.whatsNew)}
                </Button>
              </div>
            </div>
          </div>
        </header>
        <div className="mx-auto flex max-w-7xl justify-end px-6 lg:px-8">
          <FeedbackDialog>
            {({ showDialog }) => {
              return (
                <button
                  className="mr-6 inline-flex h-8 w-36 items-start justify-center gap-2.5 rounded-bl-lg rounded-br-lg bg-brand-500 px-2.5 py-1.5 text-zinc-50"
                  onClick={() => {
                    showDialog()
                  }}
                >
                  <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                  <div className="text-xs text-zinc-50">{intl.formatMessage(messages.shareFeedback)}</div>
                </button>
              )
            }}
          </FeedbackDialog>
        </div>
        {hasAnyWorkshopRole && (
          <main className="relative isolate overflow-hidden py-4">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              {fetchAppsQuery.isLoading ? (
                <Loading loadingMessage={intl.formatMessage(messages.loading)} className="h-sm" />
              ) : (
                <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-3 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:gap-8">
                  <div
                    className={cn('max-w-xl flex-col rounded-lg border border-zinc-300 p-4', {
                      'bg-zinc-100': knowledgeApps.length === 0
                    })}
                  >
                    {knowledgeApps.length > 0 ? (
                      <>
                        <div className="float-right flex items-center">
                          <Button
                            className="inline-flex rounded-3xl border border-zinc-100 bg-white text-xs font-normal text-brand-600"
                            onClick={() => {
                              seeAllKnowledge()
                            }}
                          >
                            {intl.formatMessage(messages.seeAll)}
                          </Button>
                          <Button
                            color="header"
                            onClick={() => {
                              openKnowledge()
                            }}
                          >
                            <PlusIcon className="h-5 w-5" />
                            {intl.formatMessage(defaultMessages.add)}
                          </Button>
                        </div>
                        <div className="text-2xl font-bold text-zinc-700">
                          {intl.formatMessage(messages.knowledgeSources)}
                          <Popover
                            tooltipMessage={intl.formatMessage(messages.knowledgeTooltip)}
                            background="brand"
                            className="align-middle"
                          />
                        </div>
                        <InfiniteScrollTable
                          hasNextPage={false}
                          loading={fetchAppsQuery.isLoading}
                          onLoadMore={() => {}}
                          disabled={false}
                          columns={[
                            { label: 'Name', key: 'name', headingClassName: 'text-zinc-600 text-xs font-medium' },
                            {
                              label: intl.formatMessage(messages.audience),
                              key: 'audience',
                              type: ColumnType.Audience
                            },
                            { label: intl.formatMessage(messages.ownedBy), key: 'sharedWith', type: ColumnType.Owners }
                          ]}
                          rows={knowledgeApps.slice(0, 5)}
                          onClick={(rowData: App) => {
                            openKnowledge(rowData.id)
                          }}
                        />
                      </>
                    ) : (
                      <EmptyState
                        icon={DocumentPlusIcon}
                        title={intl.formatMessage(messages.knowledgeSources)}
                        description={intl.formatMessage(messages.knowledgeTooltip)}
                        action={
                          <>
                            <PlusIcon className="h-4 w-4" />
                            {intl.formatMessage(messages.uploadADocument)}
                          </>
                        }
                        onClick={() => seeAllKnowledge()}
                      />
                    )}
                  </div>
                  <div
                    className={cn('max-w-xl flex-col rounded-lg border border-zinc-300 p-4', {
                      'bg-zinc-100': apps.length === 0
                    })}
                  >
                    {apps.length > 0 ? (
                      <>
                        <div className="float-right flex items-center">
                          <Button
                            className="inline-flex rounded-3xl border border-zinc-100 bg-white text-xs font-normal text-brand-600"
                            onClick={() => {
                              seeAllApps()
                            }}
                          >
                            {intl.formatMessage(messages.seeAll)}
                          </Button>
                          <Button
                            color="header"
                            onClick={() => {
                              newAppFromTemplate()
                            }}
                          >
                            <PlusIcon className="h-5 w-5" />
                            {intl.formatMessage(defaultMessages.add)}
                          </Button>
                        </div>
                        <div className="text-2xl font-bold text-zinc-700">
                          My recent {agentsFlagOn ? 'agents' : 'apps'}
                          <Popover
                            tooltipMessage={`${
                              agentsFlagOn ? 'Agents' : 'Apps'
                            } you own will appear here for quick access. Browse our templates to create your first ${
                              agentsFlagOn ? 'agent' : 'app'
                            }.`}
                            background="brand"
                            className="align-middle"
                          />
                        </div>
                        <AppCards apps={apps} />
                      </>
                    ) : (
                      <EmptyState
                        icon={SquaresPlusIcon}
                        title={`My recent ${agentsFlagOn ? 'agents' : 'apps'}`}
                        description={`${
                          agentsFlagOn ? 'Agents' : 'Apps'
                        } you own will appear here for quick access. Browse our templates to create your first ${
                          agentsFlagOn ? 'agent' : 'app'
                        }.`}
                        action={intl.formatMessage(messages.browseTemplates)}
                        onClick={() => newAppFromTemplate()}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        )}
      </Page>
    </>
  )
}
HomePage.displayName = 'HomePage'

export default HomePage

const AppCards = ({ apps }: { apps: App[] }) => {
  return (
    <ul className="mt-6 grid w-full grid-cols-1 gap-3">
      {map(apps.slice(0, 5), app => {
        return <AppItem key={app.id} app={app} />
      })}
    </ul>
  )
}
const AppItem = ({ app }: { app: App }) => {
  const currentSpaceId = useAppSelector(getCurrentSpaceId)
  const history = useHistory()
  const roles = useRoles()
  const currentUser = useAppSelector(getCurrentUser)
  const features = useFeatures()
  const agentsFlagOn = !!features[WORKSHOP_AGENTS]

  const templatesQuery = useQuery<{ items: Template[] }>({
    queryKey: ['/v1/appbuilder/templates']
  })

  function openApp(id: string, fromImport = false) {
    history.push(`/space/${currentSpaceId}/appbuilder/${app?.id}`, { state: { fromImport, app } })
  }
  function viewLogs(id: string) {
    history.push(`/space/${currentSpaceId}/appbuilder/${id}/logs`)
  }
  function updateApp(app: App, templateId: string | undefined) {
    history.push(`/space/${currentSpaceId}/appbuilder/templates/${templateId}`, {
      state: {
        fromUpdateVersion: true,
        isFromAppDetailsPage: false,
        appId: app.id,
        appName: app.name
      }
    })
  }
  const isUpdateAvailable = isTemplateUpdateAvailable({ app, templates: templatesQuery.data?.items })

  const hasEditPermissions =
    roles.hasWorkshopAppOwnerRole(currentSpaceId) &&
    (app.createdBy === currentUser.id || !!app.sharedWith?.includes(currentUser.id))
  const appDetailsQuery = useQuery<any>({
    queryKey: [`/v1/appbuilder/spaces/:spaceId/instances/${app.id}`]
  })

  const appDetails = appDetailsQuery.data

  const status =
    appDetails?.features?.find((feature: any) => feature.status === FeatureStatus.ACTIVE)?.status || FeatureStatus.DRAFT

  return (
    <li
      key={app.id}
      id={app.id}
      className="relative flex items-center rounded-lg border border-zinc-200 bg-white py-1 shadow-sm hover:bg-zinc-100 hover:ring-2 hover:ring-brand-400 hover:ring-offset-2"
    >
      <Button
        color="transparent"
        className="m-0 flex w-full items-start space-x-3 sm:w-full"
        onClick={() => {
          openApp(app.id)
        }}
      >
        <AppImage src={app.iconUrl || ''} size="sm" appName={app.name} />
        <div className="flex w-full flex-col">
          <div className="flex w-full items-center">
            <div className="item-center flex w-full space-x-3">
              <div className="text-lg font-bold line-clamp-1" title={app.name}>
                {app.name}
              </div>
              <div className="flex items-center pr-2">
                {isUpdateAvailable && app.template?.templateId && (
                  <NewTemplateVersionTag templateId={app.template?.templateId} appId={app.id} />
                )}
              </div>
            </div>
            <div className="flex items-center justify-end space-x-4">
              <StatusIndicator
                label={<p className="text-zinc-500">{capitalize(status)}</p>}
                variant={status === FeatureStatus.ACTIVE ? 'success' : 'default'}
              />
              <AppEditMenu
                aria-label={`${app.name} Settings`}
                onOpen={() => openApp(app.id)}
                onViewLogs={() => viewLogs(app.id)}
                onUpdateApp={() => updateApp(app, app.template?.templateId)}
                readOnly={false}
                status={app.status}
                hasUpdateAvailable={isUpdateAvailable && !!app.template?.templateId}
                hasEditPermissions={hasEditPermissions}
              />
            </div>
          </div>
          <p
            className="pt-1 text-left text-sm text-zinc-500 line-clamp-2"
            title={agentsFlagOn ? getAgentDescription(app) : app?.description}
          >
            {agentsFlagOn ? getAgentDescription(app) : app?.description}
          </p>
          {agentsFlagOn && (
            <p className="pt-1 text-left text-sm text-zinc-500 line-clamp-2">{getAgentInstructions(app)}</p>
          )}
        </div>
      </Button>
    </li>
  )
}

const EmptyState = (props: {
  icon: typeof SquaresPlusIcon
  title: ReactNode
  description: ReactNode
  action: ReactNode
  onClick: () => void
}) => {
  return (
    <div className="flex w-full flex-col justify-center pt-9 text-center">
      <div className="mx-auto mb-2 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-50">
        <props.icon className="relative flex h-8 w-8 flex-col items-start justify-start text-brand-300" />
      </div>
      <h1 className="text-zinc-950 text-2xl font-bold">{props.title}</h1>
      <h2 className="mx-auto my-4 max-w-sm text-lg font-medium text-zinc-600">{props.description}</h2>
      <Button color="primary" onClick={props.onClick} className="mx-auto flex items-center text-center">
        {props.action}
      </Button>
    </div>
  )
}
